import { useState, useEffect, useCallback, useMemo, createContext, useContext, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { logInfo } from '../lib/logger';
import { errMessage } from '../lib/net/transientError';
import { teardownAllRealtimeSyncs } from '../lib/realtime/createRealtimeSync';
import { User } from '../types';
import type { User as SupabaseUser } from '@supabase/supabase-js';

type AuthUser = User | null;

type AuthContextType = {
  user: AuthUser;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Pick<User, 'display_name' | 'avatar_url' | 'phone'>>) => Promise<{ error: Error | null }>;
  changePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Construit un user basique à partir des données auth Supabase.
 * Utilisé comme fallback immédiat si user_profiles n'est pas accessible.
 */
function buildUserFromAuth(authUser: SupabaseUser): User {
  return {
    id: authUser.id,
    email: authUser.email || '',
    role: 'admin', // Temporaire: sera remplacé par le profil DB (ou downgrade si erreur)
    created_at: authUser.created_at || new Date().toISOString(),
    display_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || undefined,
    avatar_url: authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || undefined,
    is_approved: true, // Temporaire: sera remplacé par le profil DB (ou downgrade si erreur)
    last_sign_in_at: authUser.last_sign_in_at || undefined,
  };
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Récupérer le profil depuis user_profiles (enrichit les données auth)
  const fetchAndSetProfile = useCallback(async (authUser: SupabaseUser) => {
    try {
      // SELECT * pour éviter les problèmes si certaines colonnes n'existent pas encore
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (profileError) {
        console.warn('[Auth] Profil non trouvé, accès restreint:', profileError.message);
        // Sécurité: downgrade vers un rôle non-privilégié si le profil DB est inaccessible
        setUser(prev => prev ? { ...prev, role: 'viewer' as User['role'], is_approved: false } : null);
        return;
      }

      console.log('[Auth] Profil DB chargé:', { role: profile.role, is_approved: profile.is_approved });

      // Mettre à jour avec les vraies données du profil
      setUser({
        id: profile.id,
        email: profile.email || authUser.email || '',
        role: (profile.role || 'admin') as User['role'],
        created_at: profile.created_at || authUser.created_at || new Date().toISOString(),
        display_name: profile.display_name || authUser.user_metadata?.full_name || undefined,
        avatar_url: profile.avatar_url || authUser.user_metadata?.avatar_url || undefined,
        is_approved: profile.is_approved ?? true,
        last_sign_in_at: profile.last_sign_in_at || authUser.last_sign_in_at || undefined,
        phone: profile.phone || undefined,
        updated_at: profile.updated_at || undefined,
      });
    } catch (err) {
      console.error('[Auth] Erreur fetchAndSetProfile:', err);
      // Sécurité: downgrade vers un rôle non-privilégié si le profil DB est inaccessible
      setUser(prev => prev ? { ...prev, role: 'viewer' as User['role'], is_approved: false } : null);
    }
  }, []);

  useEffect(() => {
    // 1. Récupérer la session initiale (page refresh)
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        // Set immédiat depuis auth, puis enrichir avec le profil DB
        setUser(buildUserFromAuth(session.user));
        await fetchAndSetProfile(session.user);
      }
      setLoading(false);
    });

    // 2. Écouter les changements de session (TOKEN_REFRESHED, SIGNED_OUT, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // An expired session signs out without signOut(): its channels would otherwise
        // reconnect and re-read every table anonymously, refused (401) every few minutes.
        teardownAllRealtimeSyncs();
        setUser(null);
        setLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Rafraîchir le profil en background lors du renouvellement de token
        fetchAndSetProfile(session.user);
        return;
      }

      // Pour SIGNED_IN: on ne fait rien ici, c'est signIn() qui gère
      // Cela évite la race condition entre onAuthStateChange et signIn
      if (event === 'SIGNED_IN') {
        // Si on arrive ici sans que signIn() ait été appelé 
        // (ex: restauration de session), on set le user
        if (!user && session?.user) {
          setUser(buildUserFromAuth(session.user));
          fetchAndSetProfile(session.user);
        }
        setLoading(false);
        return;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setError(null);
      console.log('[Auth] Tentative de connexion...');

      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password
      });

      if (error) {
        console.error('[Auth] Erreur signIn:', error.message);
        setError(error.message);
        return { error };
      }

      if (data.user) {
        console.log('[Auth] signIn réussi pour:', data.user.email);

        // 1. Set immédiat avec les données auth (synchrone, pas d'attente DB)
        //    Cela permet à LoginPage de naviguer tout de suite
        setUser(buildUserFromAuth(data.user));

        // 2. Enrichir avec le profil DB en background
        fetchAndSetProfile(data.user);

        // 3. Mettre à jour last_sign_in_at (fire & forget)
        supabase
          .from('user_profiles')
          .update({ last_sign_in_at: new Date().toISOString() })
          .eq('id', data.user.id)
          .then(({ error: updateErr }) => {
            if (updateErr) console.warn('[Auth] Erreur update last_sign_in_at:', updateErr.message);
          });

        // 4. Préchargement offline (warm SW cache) — best-effort, async, n'affecte pas l'UX
        //    Cf. src/lib/prefetch.ts pour la liste des endpoints prefetched.
        //    A tab left on an old build after a deploy cannot fetch this chunk any more: best-effort,
        //    so a breadcrumb, never an unhandled rejection.
        void import('../lib/prefetch')
          .then(({ prefetchForOffline }) =>
            prefetchForOffline().then((r) =>
              console.info(
                `[prefetch] ${r.contacts} contacts, ${r.opportunities} opps, ${r.appointments} RDV, ${r.notes} notes en ${r.durationMs} ms`,
              ),
            ),
          )
          .catch((prefetchErr: unknown) => logInfo('offline', 'prefetch skipped', { message: errMessage(prefetchErr) }));
      }

      return { error: null };
    } catch (err: any) {
      console.error('[Auth] Erreur inattendue signIn:', err);
      setError(err.message);
      return { error: err };
    }
  }, [fetchAndSetProfile]);

  const signOut = useCallback(async () => {
    // Drop every Realtime subscription first, so no channel keeps replaying with
    // a token that is about to be invalidated.
    teardownAllRealtimeSyncs();
    // Local scope: the default ('global') also deletes the user's OAuth sessions,
    // which disconnects every MCP client (Claude Code, claude.ai) at once.
    await supabase.auth.signOut({ scope: 'local' });
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (updates: Partial<Pick<User, 'display_name' | 'avatar_url' | 'phone'>>) => {
    const currentUser = await new Promise<AuthUser>(resolve => {
      setUser(prev => { resolve(prev); return prev; });
    });
    if (!currentUser) return { error: new Error('Non authentifié') };

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', currentUser.id);

      if (error) return { error: new Error(error.message) };

      setUser(prev => prev ? { ...prev, ...updates } : null);
      return { error: null };
    } catch (err: any) {
      return { error: err };
    }
  }, []);

  const changePassword = useCallback(async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) return { error: new Error(error.message) };
      return { error: null };
    } catch (err: any) {
      return { error: err };
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setUser(buildUserFromAuth(session.user));
      await fetchAndSetProfile(session.user);
    }
  }, [fetchAndSetProfile]);

  const contextValue = useMemo(() => ({
    user,
    loading,
    error,
    signIn,
    signOut,
    updateProfile,
    changePassword,
    refreshUser,
  }), [user, loading, error, signIn, signOut, updateProfile, changePassword, refreshUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
